import { useMemo, useSyncExternalStore } from 'react';
import {
  TUTORIAL_CHAPTERS,
  type Difficulty,
  type InfiltratorSetting,
  type MapId,
  type PlayerView,
  type Role,
} from '@il-colpo/engine';
import { create } from 'zustand';
import type { GameClient, GameMode } from './client.ts';

export type BotLevelChoice = 'auto' | 'easy' | 'medium' | 'hard' | 'expert';

export interface SoloSetup {
  mode: GameMode;
  players: 3 | 4 | 5;
  mapId: MapId;
  difficulty: Difficulty;
  infiltrator: InfiltratorSetting;
  role: Role | 'random';
  botLevel: BotLevelChoice;
  humanName: string;
  language: 'it' | 'en';
  /** Fixed seed (replays); random when absent. */
  seed?: string;
  /** Tutorial chapter (1–5): the chapter's own table, seed and plan are used. */
  chapter?: 1 | 2 | 3 | 4 | 5;
}

interface GameStore {
  client: GameClient | null;
  setup: SoloSetup | null;
  starting: boolean;
  /** Where the player goes when there is no game to show (the menu of the mode last played). */
  home: '/play' | '/online' | '/progress' | '/friends';
  start: (setup: SoloSetup) => Promise<void>;
  /** Plays a game that something else runs (online play, a replay): the screens read it like any other. */
  attach: (client: GameClient, home?: GameStore['home']) => void;
  leave: () => void;
}

/** Debug switches (speed, autopilot) only exist in development builds or when asked for. */
export const DEBUG_ENABLED = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === 'true';

function randomSeed(): string {
  const bytes = new Uint32Array(2);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36)).join('');
}

export const useGameStore = create<GameStore>((set, get) => ({
  client: null,
  setup: null,
  starting: false,
  home: '/play',
  async start(setup) {
    get().client?.dispose();
    set({ starting: true, setup, client: null, home: '/play' });
    const { createLocalSession } = await import('../local/localSession.ts');
    const params = DEBUG_ENABLED ? new URLSearchParams(window.location.search) : null;
    const speed = Number(params?.get('speed') ?? 1);
    const tutorial = setup.chapter ? TUTORIAL_CHAPTERS[setup.chapter - 1] : undefined;
    const client = createLocalSession({
      ...setup,
      botLevel: setup.botLevel,
      seed: tutorial?.seed ?? setup.seed ?? params?.get('seed') ?? randomSeed(),
      ...(tutorial ? { tutorial } : {}),
      ...(speed > 1 ? { speed } : {}),
      ...(params?.get('autopilot') === '1' ? { autopilot: true } : {}),
    });
    if (DEBUG_ENABLED) (window as unknown as { __colpo?: unknown }).__colpo = { client };
    set({ client, starting: false });
  },
  attach(client, home = '/online') {
    const old = get().client;
    if (old && old !== client) old.dispose();
    if (DEBUG_ENABLED) (window as unknown as { __colpo?: unknown }).__colpo = { client };
    set({ client, setup: null, starting: false, home });
  },
  leave() {
    get().client?.dispose();
    set({ client: null, starting: false });
  },
}));

/** The current view of the game, re-rendered on every change. Null while there is no game. */
export function useGameView(): { client: GameClient; view: PlayerView } | null {
  const client = useGameStore((s) => s.client);
  const subscribe = useMemo(
    () => (listener: () => void) => client?.subscribe(listener) ?? (() => undefined),
    [client],
  );
  useSyncExternalStore(subscribe, () => client?.version() ?? 0);
  return client ? { client, view: client.view() } : null;
}
